import { Grid, ListItem, Box } from '@mui/material'
import { EventDoc } from 'types/api/changes'
import { Model } from 'types/model'

import Event from 'components/calendar/components/Event'
import { Date, Scrollable, Time } from './styled'
import dayjs from 'dayjs'
import dateToTimeString from 'components/calendar/components'
import { t } from 'i18next'
import { isBefore, isToday, isTomorrow } from 'date-fns'
import { NoResults } from './GroupsColumn'
import { CalendarRange } from 'types/components/calendar'
import { useAppUIType } from 'components/providers/AppUITypeProvider'
import { prepareTasksForUI } from 'utils/fc_mapping'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { VariableSizeList } from 'react-window'

export interface EventsColumnProps {
  events: Model<EventDoc>[] | EventDoc[]
  showTime?: boolean
}

import { useRef, useEffect, useMemo, useCallback } from 'react'
import i18n from 'i18n'

export default function EventsColumn(props: EventsColumnProps) {
  const appui = useAppUIType()
  const listRef = useRef<VariableSizeList>(null)
  const itemSizeMap = useRef<{ [key: number]: number }>({})

  const filteredEvents = useMemo(
    () =>
      prepareTasksForUI(
        props.events,
        LocalStorage.get(LocalStorageKey.SOMEDAY_DATE) ?? '',
        LocalStorage.get(LocalStorageKey.SOMEDAY_ALL_DAY) ?? ''
      )
        ?.filter((event) => {
          if (appui.selectedLabel) {
            if (
              event.Label &&
              typeof event.Label !== 'string' &&
              event.Label.some(
                (ele) => ele.LabelText === appui.selectedLabel?.ID
              )
            ) {
              return true
            } else return false
          }
          return true
        })
        ?.sort((a, b) => {
          if (a.StartDate === b.StartDate) {
            return 0
          }
          return Number(a.StartDate) > Number(b.StartDate) ? 1 : -1
        }) || [],
    [props.events, appui.selectedLabel]
  )

  // Find the index of the first event after today
  const todayMidnight = dayjs().startOf('day').unix()
  const firstEventAfterTodayIndex = useMemo(
    () =>
      filteredEvents.findIndex(
        (event) => Number(event.StartDate) > todayMidnight
      ),
    [filteredEvents]
  )

  // Scroll to the first event after today
  useEffect(() => {
    if (firstEventAfterTodayIndex !== -1 && listRef.current) {
      listRef.current.scrollToItem(firstEventAfterTodayIndex, 'start')
    }
  }, [firstEventAfterTodayIndex])

  const getItemSize = useCallback((index: number) => {
    return itemSizeMap.current[index] || 72 // Default height with margin
  }, [])

  const setItemSize = useCallback((index: number, size: number) => {
    if (itemSizeMap.current[index] !== size) {
      itemSizeMap.current[index] = size
      listRef.current?.resetAfterIndex(index)
    }
  }, [])

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const event = filteredEvents[index]
      if (!event) return null

      const startDayjs = dayjs(Number(event.StartDate) * 1000)
      const endDayjs = dayjs(Number(event.EndDate) * 1000)

      const isBiggerThanADay = endDayjs.diff(startDayjs, 'day') > 0

      let firstLine = startDayjs.format('MMM DD, YYYY')
      let secondLine = dateToTimeString(startDayjs.toDate(), true)

      if (isToday(Number(event.StartDate) * 1000)) {
        firstLine = t('today')
      } else if (isTomorrow(Number(event.StartDate) * 1000)) {
        firstLine = t('tomorrow')
      }

      if (event.AllDay === '1') {
        secondLine = t('allDay')
      }

      if (isBiggerThanADay) {
        secondLine = endDayjs.format('MMM DD, YYYY')
      }

      const isBeforeToday = isBefore(
        startDayjs.toDate(),
        dayjs().startOf('day').toDate()
      )

      return (
        <div style={style}>
          <ListItem
            key={`${event._id}_${event.StartDate}_${event.EndDate}`}
            data-event-index={index}
            disablePadding
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              mt: '12px',
              mb: 0,
              py: 0,
              px: 0,
              height: 'auto'
            }}
            ref={(el) => {
              if (el) {
                const height = el.offsetHeight + 12 // Include margin-top
                setItemSize(index, height)
              }
            }}
          >
            {props.showTime && (
              <Grid
                display={'flex'}
                flexDirection={'column'}
                minWidth={'100px'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <Date>{firstLine}</Date>
                <Time>{secondLine}</Time>
              </Grid>
            )}
            <div
              style={{
                width: '100%',
                overflow: 'hidden',
                marginLeft: i18n.dir() === 'ltr' ? 0 : 8,
                marginRight: i18n.dir() === 'ltr' ? 8 : 0,
                opacity: isBeforeToday ? 0.5 : 1
              }}
            >
              <Event
                doNotScrollToEvent
                calendarView={CalendarRange.WEEK}
                alwaysFullSize
                event={event}
                instanceStart={Number(event.StartDate) * 1000}
                instanceEnd={Number(event.EndDate) * 1000}
                ignoreDim
                title={event.Text ?? ''}
              />
            </div>
          </ListItem>
        </div>
      )
    },
    [filteredEvents, props.showTime, setItemSize]
  )

  return props.events.length === 0 ? (
    <NoResults />
  ) : (
    <VariableSizeList
      ref={listRef}
      height={625}
      itemCount={filteredEvents.length}
      itemSize={getItemSize}
      width={'100%'}
      overscanCount={5}
    >
      {Row}
    </VariableSizeList>
  )
}
